import { useCallback, useEffect, useState } from 'react';
import { ApiClient } from '@utils/apiClient';
import { handleApiCatch, handleApiResult } from '@utils/apiErrorHandler';
import { ApiKey, ApiKeyCreated, CreateApiKeyData } from '../types';

const API_KEYS_ENDPOINT = '/users/api-keys/';

interface PaginatedApiKeys {
  results?: ApiKey[];
}

export const useApiKeys = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchApiKeys = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiClient.get(API_KEYS_ENDPOINT);
      const result = await handleApiResult<ApiKey[] | PaginatedApiKeys>(
        response,
        'Failed to fetch API keys'
      );

      if (result.success && result.data) {
        const data = result.data;
        setApiKeys(Array.isArray(data) ? data : data.results ?? []);
      } else {
        setError(result.errors?.generalError || 'Failed to fetch API keys');
      }
    } catch (err) {
      const processed = handleApiCatch(err, 'Failed to fetch API keys');
      setError(processed.generalError || 'Failed to fetch API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const createApiKey = async (data: CreateApiKeyData): Promise<ApiKeyCreated> => {
    const response = await ApiClient.post(API_KEYS_ENDPOINT, data);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message =
        errorData?.field_errors?.expiry_date?.[0] ||
        errorData?.field_errors?.name?.[0] ||
        errorData?.name?.[0] ||
        errorData?.non_field_errors?.[0] ||
        errorData?.detail ||
        'Failed to create API key';
      throw new Error(message);
    }

    const created: ApiKeyCreated = await response.json();
    await fetchApiKeys();
    return created;
  };

  const revokeApiKey = async (reference: string): Promise<void> => {
    const response = await ApiClient.delete(`${API_KEYS_ENDPOINT}${reference}/`);
    if (!response.ok) {
      const processed = await handleApiResult(response, 'Failed to revoke API key');
      throw new Error(processed.errors?.generalError || 'Failed to revoke API key');
    }
    await fetchApiKeys();
  };

  return {
    apiKeys,
    loading,
    error,
    fetchApiKeys,
    createApiKey,
    revokeApiKey,
  };
};
